import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export interface TrackingProduct {
  id: string;
  product_type: 'gps_device' | 'm2m_chip';
  name: string;
  description?: string;
  manufacturer?: string;
  model?: string;
  price_cents: number;
  subscription_price_monthly_cents?: number;
  features?: Record<string, any>;
  operators?: string[];
  has_failover?: boolean;
  data_plan_mb?: number;
  image_url?: string;
  data_plan_type?: 'basic' | 'standard' | 'premium';
  data_plan_name?: string;
  data_plan_description?: string;
  is_active: boolean;
  stock_quantity: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Campos de sincronização com Console PortalONE
  console_device_id?: string | null;
  console_synced_at?: string | null;
}

export interface TrackingProductOrder {
  id: string;
  company_id: string;
  user_id: string;
  product_id: string;
  quantity: number;
  unit_price_cents: number;
  total_price_cents: number;
  is_subscription: boolean;
  subscription_months?: number;
  subscription_start_date?: string;
  subscription_end_date?: string;
  status: string;
  payment_method?: string;
  payment_id?: string;
  paid_at?: string;
  shipping_address?: any;
  tracking_code?: string;
  shipped_at?: string;
  delivered_at?: string;
  metadata?: any;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProductOrderInput {
  company_id: string;
  product_id: string;
  quantity: number;
  is_subscription?: boolean;
  subscription_months?: number;
  shipping_address?: any;
  notes?: string;
}

// Fetch all tracking products or filter by type
export function useTrackingProducts(productType?: 'gps_device' | 'm2m_chip') {
  return useQuery({
    queryKey: ['tracking-products', productType],
    queryFn: async () => {
      let query = supabase
        .from('tracking_products')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      
      if (productType) {
        query = query.eq('product_type', productType);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching tracking products:', error);
        throw error;
      }
      
      return data as TrackingProduct[];
    },
  });
}

// Fetch a single product
export function useTrackingProduct(productId?: string) {
  return useQuery({
    queryKey: ['tracking-product', productId],
    queryFn: async () => {
      if (!productId) return null;
      
      const { data, error } = await supabase
        .from('tracking_products')
        .select('*')
        .eq('id', productId)
        .single();
      
      if (error) {
        console.error('Error fetching tracking product:', error);
        throw error;
      }
      
      return data as TrackingProduct;
    },
    enabled: !!productId,
  });
}

// Fetch orders for a company
export function useTrackingProductOrders(companyId?: string) {
  return useQuery({
    queryKey: ['tracking-product-orders', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('tracking_product_orders')
        .select('*, tracking_products(*)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching tracking product orders:', error);
        throw error;
      }
      
      return data;
    },
    enabled: !!companyId,
  });
}

// Create a new product order
export function useCreateProductOrder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (orderData: CreateProductOrderInput) => {
      // Fetch product to get pricing
      const { data: product, error: productError } = await supabase
        .from('tracking_products')
        .select('*')
        .eq('id', orderData.product_id)
        .single();
      
      if (productError) throw productError;
      if (!product) throw new Error('Product not found');
      
      // Calculate prices
      const unitPrice = orderData.is_subscription && product.subscription_price_monthly_cents
        ? product.subscription_price_monthly_cents
        : product.price_cents;
      
      const totalPrice = unitPrice * orderData.quantity;
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Create order
      const { data, error } = await supabase
        .from('tracking_product_orders')
        .insert({
          company_id: orderData.company_id,
          user_id: user.id,
          product_id: orderData.product_id,
          quantity: orderData.quantity,
          unit_price_cents: unitPrice,
          total_price_cents: totalPrice,
          is_subscription: orderData.is_subscription || false,
          subscription_months: orderData.subscription_months,
          shipping_address: orderData.shipping_address,
          notes: orderData.notes,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tracking-product-orders', data.company_id] });
      toast({
        title: 'Pedido criado com sucesso!',
        description: 'Seu pedido foi registrado e está aguardando pagamento.',
      });
    },
    onError: (error: any) => {
      console.error('Error creating product order:', error);
      toast({
        title: 'Erro ao criar pedido',
        description: error.message || 'Ocorreu um erro ao criar o pedido. Tente novamente.',
        variant: 'destructive',
      });
    },
  });
}

// Update order status
export function useUpdateProductOrder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ orderId, updates }: { orderId: string; updates: Partial<TrackingProductOrder> }) => {
      const { data, error } = await supabase
        .from('tracking_product_orders')
        .update(updates)
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tracking-product-orders', data.company_id] });
      toast({
        title: 'Pedido atualizado!',
        description: 'O status do pedido foi atualizado com sucesso.',
      });
    },
    onError: (error: any) => {
      console.error('Error updating product order:', error);
      toast({
        title: 'Erro ao atualizar pedido',
        description: error.message || 'Ocorreu um erro ao atualizar o pedido.',
        variant: 'destructive',
      });
    },
  });
}

// Admin: Create product
export function useCreateProduct() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (product: any) => {
      const { data, error } = await supabase
        .from('tracking_products')
        .insert(product)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracking-products'] });
      toast({
        title: 'Produto criado com sucesso!',
      });
    },
    onError: (error: any) => {
      console.error('Error creating product:', error);
      toast({
        title: 'Erro ao criar produto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Admin: Update product
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { data, error } = await supabase
        .from('tracking_products')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracking-products'] });
      toast({
        title: 'Produto atualizado com sucesso!',
      });
    },
    onError: (error: any) => {
      console.error('Error updating product:', error);
      toast({
        title: 'Erro ao atualizar produto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Admin: Delete product
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (productId: string) => {
      const { error } = await supabase
        .from('tracking_products')
        .delete()
        .eq('id', productId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracking-products'] });
      toast({
        title: 'Produto excluído com sucesso!',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting product:', error);
      toast({
        title: 'Erro ao excluir produto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
